import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { Sparkles, UserCheck, Loader2 } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import {
  getPopulationRequirement,
  withAuditorPopulationOverride,
} from '../../features/controls/populationRequirement.ts';

function currentUserName() {
  try {
    return localStorage.getItem('username') || null;
  } catch {
    return null;
  }
}

/**
 * PopulationRequirementPanel — "Population" card on the Test Plan tab.
 *
 * Shows whether test runs for this control need a population file. The AI proposes
 * a value when test attributes are generated; the auditor can flip it at any time.
 * Existing runs keep their own population status — a change here only affects runs
 * created afterwards.
 */
export default function PopulationRequirementPanel({ control, controlUuid, onControlUpdated, readOnly = false }) {
  const req = getPopulationRequirement(control);
  const [saving, setSaving] = useState(false);

  const setRequired = async (required) => {
    if (required === req.required || saving || !controlUuid) return;
    setSaving(true);
    try {
      await axiosInstance.put(`/api/sox-controls/${controlUuid}`, {
        sampling_attributes: withAuditorPopulationOverride(control?.sampling_attributes, required, currentUserName()),
      });
      toast.success(required
        ? 'New test runs will require a population file.'
        : 'New test runs will use evidence files as samples.');
      onControlUpdated?.();
    } catch (err) {
      toast.error(resolve403ErrorMessage(err, err.response?.data?.error || err.message || 'Failed to save'));
    } finally {
      setSaving(false);
    }
  };

  const optionCls = (active) =>
    `flex-1 text-left px-3 py-2 rounded-lg border text-sm transition-colors disabled:opacity-60 ${
      active ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
    }`;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-6 py-4" data-testid="population-requirement-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Population for test runs</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Controls that operate on many items need a population file so a sample can be drawn.
            One-off or design-only controls can be tested directly from evidence files.
          </p>
        </div>
        <span className="shrink-0 inline-flex items-center gap-1 text-xs text-gray-500">
          {saving ? (
            <><Loader2 className="h-3 w-3 animate-spin" /> Saving…</>
          ) : req.source === 'auditor' ? (
            <><UserCheck className="h-3 w-3" /> Set by {req.setBy || 'auditor'}</>
          ) : req.source === 'ai' ? (
            <><Sparkles className="h-3 w-3 text-blue-500" /> AI suggested</>
          ) : (
            <>Default</>
          )}
        </span>
      </div>

      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <button
          type="button"
          disabled={readOnly || saving}
          onClick={() => setRequired(true)}
          className={optionCls(req.required)}
          aria-pressed={req.required}
        >
          <span className="font-medium">Population file required</span>
          <span className="block text-xs text-gray-500">Upload the population, verify completeness (IPE), then select a sample.</span>
        </button>
        <button
          type="button"
          disabled={readOnly || saving}
          onClick={() => setRequired(false)}
          className={optionCls(!req.required)}
          aria-pressed={!req.required}
        >
          <span className="font-medium">No population needed</span>
          <span className="block text-xs text-gray-500">Each group of evidence files you upload becomes a sample.</span>
        </button>
      </div>

      {req.rationale && (
        <p className="mt-3 text-xs text-gray-600">
          <span className="font-medium text-gray-700">AI rationale:</span> {req.rationale}
          {req.confidence && <span className="text-gray-400"> ({req.confidence} confidence)</span>}
        </p>
      )}
    </div>
  );
}
